import React, { useEffect, useState } from "react";
import { useForm } from "@formspree/react";
import { useTranslation } from "react-i18next";
import { useFormValidation, VALIDATION_RULES } from "../../hooks/useFormValidation";
import "./ContactUs.css";

function ContactUs() {
  const { i18n } = useTranslation();
  const [firstLoadLanguage, setFirstLoadLanguage] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const userHasChosenLanguage = localStorage.getItem("userLanguageChoice");

    if (!userHasChosenLanguage && i18n.language === "en") {
      setFirstLoadLanguage("ru");
    } else {
      setFirstLoadLanguage(i18n.language);
    }
  }, [i18n.language]);

  const t = i18n.getFixedT(firstLoadLanguage || i18n.language);

  const [state] = useForm("mzbnnlwr");

  // Use secure form validation
  const {
    formData,
    errors,
    isSubmitting,
    updateField,
    handleSubmit: handleSecureSubmit,
    resetForm
  } = useFormValidation(
    {
      name: "",
      lastName: "",
      phone: "",
      email: "",
      message: "",
    },
    {
      name: { ...VALIDATION_RULES.name, required: 'This field is required' },
      lastName: { ...VALIDATION_RULES.name, required: 'This field is required' },
      phone: { 
        ...VALIDATION_RULES.phone, 
        required: 'This field is required',
        minLength: 7, // Require at least 7 characters
        minDigits: 7, // Require at least 7 digits (we'll validate this in validateField)
        pattern: /^[+]?[\d\s\-()]{7,20}$/,
        patternMessage: "Please enter a valid phone number (at least 7 digits)",
        validate: (value) => {
          if (!value || value.trim() === '') {
            return 'This field is required';
          }
          // Remove all non-digit characters to count actual digits
          const digitsOnly = value.replace(/\D/g, '');
          if (digitsOnly.length < 7) {
            return 'Phone number must contain at least 7 digits';
          }
          if (digitsOnly.length > 15) {
            return 'Phone number must not exceed 15 digits';
          }
          // Check the pattern
          if (!/^[+]?[\d\s\-()]{7,20}$/.test(value)) {
            return 'Please enter a valid phone number';
          }
          return true;
        }
      },
      email: { ...VALIDATION_RULES.email, required: 'This field is required' },
      message: { ...VALIDATION_RULES.message, required: 'This field is required' },
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Trigger validation - this will set errors if validation fails
    const success = await handleSecureSubmit(async (data) => {
      try {
        // Create FormData for Formspree
        const formDataToSubmit = new FormData();
        formDataToSubmit.append('name', data.name.trim());
        formDataToSubmit.append('lastName', data.lastName.trim());
        formDataToSubmit.append('phone', data.phone.trim());
        formDataToSubmit.append('email', data.email.trim().toLowerCase());
        formDataToSubmit.append('message', data.message.trim());
        
        // Submit to Formspree
        const response = await fetch('https://formspree.io/f/mzbnnlwr', {
          method: 'POST',
          body: formDataToSubmit,
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Form submission failed');
        }
        
        // If successful, update local state
        setSubmitSuccess(true);
        resetForm();
      } catch (error) {
        console.error("Form submission error:", error);
        // Error is handled - form will show validation errors
      }
    });

    if (!success) {
      // Form validation failed - scroll to first error field
      setTimeout(() => {
        // Find the first field with an error by checking the errors object
        const fieldOrder = ['name', 'lastName', 'phone', 'email', 'message'];
        let firstErrorFieldName = null;
        
        for (const fieldName of fieldOrder) {
          if (errors[fieldName]) {
            firstErrorFieldName = fieldName;
            break;
          }
        }
        
        if (firstErrorFieldName) {
          // Find the form group for the first error field
          const fieldElement = document.getElementById(firstErrorFieldName);
          if (fieldElement) {
            const formGroup = fieldElement.closest('.form-group-contact-us');
            if (formGroup) {
              // Scroll to the form group with smooth behavior
              formGroup.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });
              
              // Focus the input/textarea after scrolling
              setTimeout(() => {
                fieldElement.focus();
              }, 300);
            }
          }
        }
      }, 100);
    }
  };

  if (state.succeeded || submitSuccess) {
    return (
      <p className="contact-us-reply">{t("we_will_be_back_to_you_soon")}</p>
    );
  }
  // const { t } = useTranslation();
  // const [state, handleSubmit] = useForm("mzbnnlwr");

  // if (state.succeeded) {
  //   return (
  //     <p className="contact-us-reply">{t("we_will_be_back_to_you_soon")}</p>
  //   );
  // }

  return (
    <section>
      <div className="contact-us-header-container">
        <h2 className="contact-us-header">{t("contact_us_header")}</h2>
        <p className="contact-us-description">{t("contact_us_description")}</p>
        <p className="contact-us-required-note">{t("contact_us_all_fields_required")}</p>
      </div>
      <form
        onSubmit={handleSubmit}
        className="contact-form"
      >
        <div className="form-group-contact-us">
          <label htmlFor="name">{t("name")} <span style={{ color: 'red' }}>*</span></label>
          <input
            id="name"
            type="text"
            name="name"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder={t("name_placeholder")}
            className={`required-field ${errors.name ? "error" : ""}`}
            disabled={isSubmitting}
          />
          {errors.name && (
            <span className="validation-error">{errors.name}</span>
          )}
        </div>

        <div className="form-group-contact-us">
          <label htmlFor="lastName">{t("last_name")} <span style={{ color: 'red' }}>*</span></label>
          <input
            id="lastName"
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={(e) => updateField('lastName', e.target.value)}
            placeholder={t("last_name_placeholder")}
            className={`required-field ${errors.lastName ? "error" : ""}`}
            disabled={isSubmitting}
          />
          {errors.lastName && (
            <span className="validation-error">{errors.lastName}</span>
          )}
        </div>

        <div className="form-group-contact-us">
          <label htmlFor="phone">{t("phone_number")} <span style={{ color: 'red' }}>*</span></label>
          <input
            id="phone"
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            placeholder={t("phone_placeholder")}
            className={`required-field ${errors.phone ? "error" : ""}`}
            disabled={isSubmitting}
          />
          {errors.phone && (
            <span className="validation-error">{errors.phone}</span>
          )}
        </div>

        <div className="form-group-contact-us">
          <label htmlFor="email">{t("email_address")} <span style={{ color: 'red' }}>*</span></label>
          <input
            id="email"
            type="email"
            name="email"
            value={formData.email}
            onChange={(e) => updateField('email', e.target.value)}
            placeholder={t("email_placeholder")}
            className={`required-field ${errors.email ? "error" : ""}`}
            disabled={isSubmitting}
          />
          {errors.email && (
            <span className="validation-error">{errors.email}</span>
          )}
        </div>

        <div className="form-group-contact-us full-width">
          <label htmlFor="message">{t("your_message")} <span style={{ color: 'red' }}>*</span></label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={(e) => updateField('message', e.target.value)}
            placeholder={t("message_placeholder")}
            className={`required-field ${errors.message ? "error" : ""}`}
            disabled={isSubmitting}
          />
          {errors.message && (
            <span className="validation-error">{errors.message}</span>
          )}
        </div>

        <button
          className="submit-contact-us"
          type="submit"
          disabled={isSubmitting || state.submitting}
        >
          {isSubmitting ? t("submitting") : t("submit")}
        </button>
      </form>
    </section>
  );
}

export default ContactUs;
